/**
 * Dainty theme — core runtime (loaded on every page).
 *
 * Exposes `window.Theme` (config, cart API, helpers) for feature modules, and
 * defines the small custom elements used site-wide:
 *   <drawer-dialog>    modal drawers (menu, cart, filters, search)
 *   <carousel-slider>  scroll-snap carousel with external prev/next controls
 * Feature modules (header.js, cart.js, product.js, facets.js, search.js) are
 * loaded only by the sections that need them.
 */

const config = JSON.parse(document.getElementById('ThemeConfig')?.textContent || '{}');

const announce = (message) => {
  const region = document.getElementById('LiveRegion');
  if (!region) return;
  region.textContent = '';
  requestAnimationFrame(() => (region.textContent = message));
};

const debounce = (fn, wait = 250) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
};

const fetchSection = async (url, sectionId) => {
  const target = new URL(url, window.location.origin);
  target.searchParams.set('section_id', sectionId);
  const response = await fetch(target);
  if (!response.ok) throw new Error(`Section ${sectionId} failed: ${response.status}`);
  return new DOMParser().parseFromString(await response.text(), 'text/html');
};

/* ------------------------------------------------------------------ */
/* Cart API                                                            */
/* ------------------------------------------------------------------ */

const cartRequest = async (url, body) => {
  const response = await fetch(url, {
    method: 'POST',
    headers: { Accept: 'application/json', ...(body instanceof FormData ? {} : { 'Content-Type': 'application/json' }) },
    body: body instanceof FormData ? body : JSON.stringify(body),
  });
  const data = await response.json();
  if (!response.ok || data.status) {
    throw new Error(data.description || data.message || config.strings?.cartError);
  }
  return data;
};

const cart = {
  sections: () =>
    [...document.querySelectorAll('[data-cart-section]')].map((el) => el.dataset.cartSection).filter(Boolean),

  async add(formData) {
    formData.set('sections', this.sections().join(','));
    formData.set('sections_url', window.location.pathname);
    const data = await cartRequest(`${config.routes.cartAdd}`, formData);
    this.publish(data, 'add');
    return data;
  },

  /** Change a line by its unique key (stable across re-renders, unlike line index). */
  async change(key, quantity) {
    const data = await cartRequest(`${config.routes.cartChange}`, {
      id: key,
      quantity,
      sections: this.sections(),
      sections_url: window.location.pathname,
    });
    this.publish(data, 'change');
    return data;
  },

  /**
   * Update cart attributes / note. Pass `{ render: false }` for background
   * saves (e.g. typing a note) so the UI isn't re-rendered under the user.
   */
  async update(updates, { render = true } = {}) {
    const body = render ? { ...updates, sections: this.sections(), sections_url: window.location.pathname } : updates;
    const data = await cartRequest(`${config.routes.cartUpdate}`, body);
    if (render) this.publish(data, 'update');
    return data;
  },

  publish(data, source) {
    const focus = this.focusSnapshot();
    if (data.sections) {
      for (const [id, html] of Object.entries(data.sections)) {
        if (!html) continue;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const current = document.getElementById(`shopify-section-${id}`);
        if (!current) continue;
        // Swap only the live region of the section so open dialogs stay open.
        current.querySelectorAll('[data-cart-content]').forEach((target) => {
          const next = doc.querySelector(`[data-cart-content="${target.dataset.cartContent}"]`);
          if (next) target.replaceWith(next);
        });
      }
    }
    this.restoreFocus(focus);
    const count = data.item_count ?? document.querySelector('[data-cart-item-count]')?.dataset.cartItemCount;
    if (count !== undefined) {
      this.setCount(Number(count));
    } else {
      fetch(`${config.routes.cart}.js`)
        .then((response) => response.json())
        .then((state) => this.setCount(state.item_count))
        .catch(() => {});
    }
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: data, source } }));
  },

  /** Remember which cart control had focus so it can be restored after a re-render. */
  focusSnapshot() {
    const active = document.activeElement;
    const region = active?.closest?.('[data-cart-content]');
    if (!region) return null;
    const line = active.closest('[data-key]');
    const control = ['data-quantity-step', 'data-quantity-input', 'data-line-remove'].find((attr) => active.hasAttribute(attr));
    return { region: region.dataset.cartContent, key: line?.dataset.key, control, step: active.dataset.quantityStep };
  },

  restoreFocus(snapshot) {
    if (!snapshot) return;
    const region = document.querySelector(`[data-cart-content="${snapshot.region}"]`);
    if (!region) return;
    let target = null;
    if (snapshot.key && snapshot.control) {
      const line = region.querySelector(`[data-key="${CSS.escape(snapshot.key)}"]`);
      const selector = snapshot.control === 'data-quantity-step' ? `[data-quantity-step="${snapshot.step}"]` : `[${snapshot.control}]`;
      target = line?.querySelector(selector);
    }
    (target || region.querySelector('h1, h2, [tabindex="-1"]'))?.focus({ preventScroll: true });
  },

  setCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count < 1;
    });
  },
};

/* ------------------------------------------------------------------ */
/* <drawer-dialog>                                                     */
/* ------------------------------------------------------------------ */

class DrawerDialog extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog || event.target.closest('[data-drawer-close]')) this.close();
    });
    this.dialog.addEventListener('close', () => {
      this.opener?.setAttribute('aria-expanded', 'false');
      this.opener?.focus({ preventScroll: true });
    });
  }

  open(opener) {
    if (!this.dialog || this.dialog.open) return;
    this.opener = opener;
    opener?.setAttribute('aria-expanded', 'true');
    this.dialog.showModal();
    this.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
  }

  close() {
    this.dialog?.close();
  }
}

customElements.define('drawer-dialog', DrawerDialog);

document.addEventListener('click', (event) => {
  const opener = event.target.closest('[data-drawer-open]');
  if (!opener) return;
  const drawer = document.getElementById(opener.dataset.drawerOpen);
  if (!drawer) return;
  event.preventDefault();
  drawer.open(opener);
});

/* ------------------------------------------------------------------ */
/* <carousel-slider>                                                   */
/* ------------------------------------------------------------------ */

class CarouselSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-carousel-track]') || this;
    this.prev = document.querySelectorAll(`[data-carousel-prev="${this.id}"]`);
    this.next = document.querySelectorAll(`[data-carousel-next="${this.id}"]`);

    this.prev.forEach((btn) => btn.addEventListener('click', () => this.go(-1)));
    this.next.forEach((btn) => btn.addEventListener('click', () => this.go(1)));

    this.update = this.update.bind(this);
    this.track.addEventListener('scroll', debounce(this.update, 60), { passive: true });
    this.observer = new ResizeObserver(this.update);
    this.observer.observe(this.track);

    const delay = Number(this.getAttribute('autoplay'));
    if (delay && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.paused = false;
      this.userPaused = false;
      this.addEventListener('pointerenter', () => (this.paused = true));
      this.addEventListener('pointerleave', () => (this.paused = this.userPaused));
      this.addEventListener('focusin', () => (this.paused = true));
      this.addEventListener('focusout', () => (this.paused = this.userPaused));
      this.querySelector('[data-carousel-toggle]')?.addEventListener('click', (event) => {
        this.userPaused = !this.userPaused;
        this.paused = this.userPaused;
        event.currentTarget.setAttribute('aria-pressed', String(this.userPaused));
      });
      this.timer = setInterval(() => this.advance(), delay);
    }
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    clearInterval(this.timer);
  }

  advance() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    if (this.paused || scrollWidth <= clientWidth + 2) return;
    if (scrollLeft + clientWidth >= scrollWidth - 2) {
      this.track.scrollTo({ left: 0, behavior: 'smooth' });
    } else {
      this.go(1);
    }
  }

  get step() {
    const item = this.track.firstElementChild;
    if (!item) return this.track.clientWidth;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const perView = Math.max(1, Math.round(this.track.clientWidth / (item.offsetWidth + gap)));
    return (item.offsetWidth + gap) * perView;
  }

  go(direction) {
    this.track.scrollBy({ left: this.step * direction, behavior: 'smooth' });
  }

  update() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    this.prev.forEach((btn) => (btn.disabled = scrollLeft <= 2));
    this.next.forEach((btn) => (btn.disabled = scrollLeft + clientWidth >= scrollWidth - 2));
  }
}

customElements.define('carousel-slider', CarouselSlider);

/* ------------------------------------------------------------------ */
/* Product card swatches: preview variant image on hover / focus       */
/* ------------------------------------------------------------------ */

const previewSwatch = (event) => {
  const swatch = event.target.closest?.('[data-card-swatch]');
  if (!swatch) return;
  const card = swatch.closest('[data-product-card]');
  if (!card) return;
  const image = card.querySelector('.media img');
  const link = card.querySelector('[data-card-link]');

  card.querySelectorAll('[data-card-swatch]').forEach((el) => el.removeAttribute('aria-current'));
  swatch.setAttribute('aria-current', 'true');
  if (link) link.href = swatch.href;
  if (image && swatch.dataset.src) {
    image.srcset = swatch.dataset.srcset || '';
    image.src = swatch.dataset.src;
  }
};

document.addEventListener('mouseover', previewSwatch);
document.addEventListener('focusin', previewSwatch);

/* ------------------------------------------------------------------ */
/* Forms that submit on change (localization, sort)                    */
/* ------------------------------------------------------------------ */

document.addEventListener('change', (event) => {
  if (event.target.matches?.('[data-autosubmit]')) event.target.form?.requestSubmit();
});

/* ------------------------------------------------------------------ */
/* Sale code auto-apply (Theme settings → Sale pricing)                */
/* Applies the sale code to the cart so cart, drawer and checkout show */
/* the discounted price. Re-applied after adds; skipped if the shopper */
/* already has a code on the cart. An unlocked code (unlock-offer.js) */
/* takes over from the sale code.                                     */
/* ------------------------------------------------------------------ */

const unlockedCode = () => {
  try {
    return config.unlockCode && localStorage.getItem('theme:unlocked-code') === config.unlockCode ? config.unlockCode : null;
  } catch {
    return null;
  }
};

const applySaleCode = async () => {
  const code = unlockedCode() || config.saleCode;
  if (!code) return;
  try {
    const state = await (await fetch(`${config.routes.cart}.js`)).json();
    if (!state.item_count) return;
    const codes = (state.discount_codes || []).map((d) => d.code.toUpperCase());
    if (codes.includes(code.toUpperCase())) return;
    if (codes.some((applied) => applied !== config.saleCode?.toUpperCase())) return;
    await cart.update({ discount: code });
  } catch {
    /* Non-blocking: the code can still be entered at checkout. */
  }
};

document.addEventListener('cart:updated', ({ detail }) => {
  if (detail.source === 'add') applySaleCode();
});
applySaleCode();

/* ------------------------------------------------------------------ */
/* Copy to clipboard ([data-copy], e.g. discount codes)               */
/* ------------------------------------------------------------------ */

document.addEventListener('click', async (event) => {
  const button = event.target.closest('[data-copy]');
  if (!button) return;
  try {
    await navigator.clipboard.writeText(button.dataset.copy);
    const label = button.querySelector('[data-copy-label]');
    const original = label?.textContent;
    if (label) label.textContent = button.dataset.copiedLabel || original;
    announce(button.dataset.copiedLabel || '');
    setTimeout(() => label && (label.textContent = original), 2000);
  } catch {
    /* Clipboard unavailable: the code stays visible to copy manually. */
  }
});

/* ------------------------------------------------------------------ */
/* Analytics bridge: Klaviyo "Added to Cart" (replaces legacy snippet) */
/* ------------------------------------------------------------------ */

document.addEventListener('cart:updated', ({ detail }) => {
  if (detail.source !== 'add') return;
  const item = detail.cart?.items?.[0] || detail.cart;
  if (!item?.product_title) return;
  const payload = {
    $value: (item.final_line_price ?? item.price) / 100,
    AddedItemProductName: item.product_title,
    AddedItemProductID: item.product_id,
    AddedItemSKU: item.sku,
    AddedItemVariantName: item.variant_title,
    AddedItemImageURL: item.image,
    AddedItemURL: new URL(item.url, window.location.origin).href,
    AddedItemPrice: item.final_price / 100,
    AddedItemQuantity: item.quantity,
  };
  if (window.klaviyo?.track) window.klaviyo.track('Added to Cart', payload);
  else (window._learnq = window._learnq || []).push(['track', 'Added to Cart', payload]);
});

/* ------------------------------------------------------------------ */

window.Theme = Object.freeze({ config, cart, announce, debounce, fetchSection });

document.documentElement.classList.replace('no-js', 'js');
